import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import TherapistSidebar from "../../components/TherapistSidebar";
import { Link } from "react-router-dom";


const intervalOptions = [
  { label: "Săptămâna curentă", value: "this_week" },
  { label: "Săptămâna trecută", value: "last_week" },
  { label: "Luna curentă", value: "this_month" },
  { label: "Luna trecută", value: "last_month" },
  { label: "Interval custom", value: "custom" },
];

const getStartEndDates = (type: string) => {
  const today = new Date();
  let start: Date;
  let end: Date;

  const cloneDate = (d: Date) => new Date(d.getTime());
  const getMondayOfWeek = (date: Date) => {
    const d = cloneDate(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  if (type === "this_week") {
    start = getMondayOfWeek(today);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (type === "last_week") {
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    start = getMondayOfWeek(lastWeek);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (type === "this_month") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (type === "last_month") {
    const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    start = new Date(year, month, 1);
    end = new Date(year, month + 1, 0);
  } else {
    start = cloneDate(today);
    end = cloneDate(today);
  }

  const toDateString = (d: Date, time: "start" | "end") => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return time === "start" ? `${year}-${month}-${day}T00:00:00` : `${year}-${month}-${day}T23:59:59.999`;
  };

  return {
    start_time: toDateString(start, "start"),
    end_time: toDateString(end, "end"),
  };
};

interface Therapist {
  user_id: string;
  firstname: string;
  lastname: string;
  is_active: boolean;
}

interface TherapistScore {
  therapist_id: string;
  total_score: number;
}

const TherapistStandingsPage: React.FC = () => {
  const { userId, token } = useAuth();
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [scores, setScores] = useState<TherapistScore[]>([]);
  const [intervalType, setIntervalType] = useState("this_week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedTherapist") === "true";
  });

  useEffect(() => {
    const fetchTherapists = async () => {
      const res = await fetch(`${API_BASE_URL}/users/therapists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTherapists(data || []);
    };
    fetchTherapists();
  }, [token]);

  useEffect(() => {
    const fetchScores = async () => {
      let start_time = "", end_time = "";
      if (intervalType === "custom") {
        if (!customStart || !customEnd) return;
        start_time = `${customStart}T00:00:00`;
        end_time = `${customEnd}T23:59:59.999`;
      } else {
        const range = getStartEndDates(intervalType);
        start_time = range.start_time;
        end_time = range.end_time;
      }

      const res = await fetch(`${API_BASE_URL}/score-config/total-by-therapist?start_time=${start_time}&end_time=${end_time}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setScores(data || []);
    };

    fetchScores();
  }, [token, intervalType, customStart, customEnd]);

  const getScoreForTherapist = (id: string): number => {
    const score = scores.find((s) => s.therapist_id === id);
    return score?.total_score || 0;
  };

  const filteredTherapists =  therapists.filter((t: any) => t.is_active);

  // Sortare terapeuti dupa scor descrescator
  const sortedTherapists = [...filteredTherapists].sort((a, b) =>
    getScoreForTherapist(b.user_id) - getScoreForTherapist(a.user_id)
  );
  

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <TherapistSidebar onCollapseChange={setIsCollapsed} />
      <div className="page-content">
        <h1 className="page-title">Clasament Terapeuți</h1>

        <div className="interval-container">
          <label>Interval: </label>
          <select value={intervalType} onChange={(e) => setIntervalType(e.target.value)}>
            {intervalOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {intervalType === "custom" && (
            <>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            </>
          )}
        </div>

        <div className="table-rounded-container">
          <table className="table-rounded table-therapist-standings">
            <thead>
              <tr>
                <th className="rank">#</th>
                <th className="name">Terapeut</th>
                <th className="score">Punctaj total</th>
              </tr>
            </thead>
            <tbody>
              {sortedTherapists.map((t, index) => (
                <tr
                  key={t.user_id}
                  className={t.user_id === userId ? "highlighted" : ""}
                >
                  <td className="rank">{index + 1}</td>
                  <td className="name">
                    <Link to={`/therapist/therapist-points/${t.user_id}`}>
                      {t.firstname} {t.lastname}
                    </Link>
                  </td>
                  <td className="score">
                    {getScoreForTherapist(t.user_id)} puncte
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TherapistStandingsPage;
